/**
 * A table layout that belongs to the person, not to the browser.
 *
 * `useGridPrefs('grid:pipeline', defaults)` hands back `{ prefs, set, reset,
 * loaded, defaults }`. The table draws with the defaults at once; whatever was
 * saved (GET /api/me/prefs/:key) is laid over them when it arrives, so a slow
 * answer costs a re-arrangement rather than a spinner. Every change is saved
 * 600ms after the last one (PUT /api/me/prefs/:key), so dragging a column
 * wider is one write, not sixty.
 *
 * A copy also lives in this browser. Offline there is no server, and while
 * the route is missing or failing a layout should still survive a reload -
 * so the copy remembers whether it reached the server, and one that did not
 * is sent up at the next load instead of being overwritten there by an
 * older value. Copies are kept per signed-in user, so on a shared machine
 * one person's columns do not become the next person's.
 *
 * Nothing here throws. A layout is a convenience; failing to load one or to
 * save one must never take the table down with it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import api, { sessionUser } from '../../lib/api.js';
import { isOffline } from '../../lib/storage.js';

const SAVE_AFTER = 600;

/* ------------------------------------------------------ browser copy */

const copyKey = key => `client-ledger:pref:${(sessionUser() || {}).id || 'local'}:${key}`;

function readCopy(key) {
  try {
    const entry = JSON.parse(localStorage.getItem(copyKey(key)) || 'null');
    return entry && typeof entry === 'object' && entry.value && typeof entry.value === 'object' ? entry : null;
  } catch {
    return null;
  }
}

function writeCopy(key, entry) {
  try { localStorage.setItem(copyKey(key), JSON.stringify(entry)); } catch { /* full, or private mode */ }
}

/* ------------------------------------------------------------ server */

/*
 * One write at a time per key. Two saves in flight can land in either
 * order, and the older one landing last would put back a layout somebody
 * had already changed.
 */
const queues = new Map();

/** Save `value` under `key`: here at once, then to the server. Never rejects. */
export function writePrefs(key, value) {
  writeCopy(key, { value, pending: true });
  if (isOffline()) return Promise.resolve();
  const next = (queues.get(key) || Promise.resolve())
    .then(() => api.me.prefs.put(key, value))
    .then(() => {
      /* Only mark it sent if nothing newer has been written meanwhile. */
      const now = readCopy(key);
      if (now && now.value && now.value.at === value.at) writeCopy(key, { value, pending: false });
    })
    .catch(() => { /* stays pending; sent at the next load */ });
  queues.set(key, next);
  return next;
}

/** What is saved under `key`, reconciling this browser's copy with the
    server's. Resolves null when nothing is; never rejects. */
export async function readPrefs(key) {
  const mine = readCopy(key);
  if (isOffline()) return mine ? mine.value : null;

  let theirs;
  try {
    const res = await api.me.prefs.get(key);
    theirs = res && res.value && typeof res.value === 'object' ? res.value : null;
  } catch {
    /* No route yet, no network, not signed in: the copy stands in. */
    return mine ? mine.value : null;
  }

  /*
   * The copy wins only if it never got through and is newer than what the
   * server holds - or the server holds nothing, which is also what a new
   * database looks like the day this route appears.
   */
  if (mine && (!theirs || (mine.pending && (mine.value.at || 0) > (theirs.at || 0)))) {
    writePrefs(key, mine.value);
    return mine.value;
  }
  if (theirs) writeCopy(key, { value: theirs, pending: false });
  return theirs;
}

/* -------------------------------------------------------------- hook */

/**
 * `key` may be null, which makes the hook inert: it holds the defaults and
 * saves nothing. That lets a component call it unconditionally and use a
 * layout it was handed instead.
 */
export default function useGridPrefs(key, defaults = {}) {
  const base = useRef(defaults);
  base.current = defaults;

  const [prefs, setPrefs] = useState(() => ({ ...defaults }));
  const [loaded, setLoaded] = useState(!key);
  const touched = useRef(false);
  const waiting = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!key) return undefined;
    let live = true;
    touched.current = false;
    readPrefs(key)
      .then(saved => {
        /* A change made while this was loading is newer than anything saved,
           so it is not thrown away for it. */
        if (live && !touched.current && saved) setPrefs({ ...base.current, ...saved });
      })
      .catch(() => {})
      .finally(() => { if (live) setLoaded(true); });
    return () => { live = false; };
  }, [key]);

  const flush = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = null;
    const value = waiting.current;
    waiting.current = null;
    if (key && value) writePrefs(key, value);
  }, [key]);

  useEffect(() => {
    if (!key || !touched.current) return;
    waiting.current = prefs;
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, SAVE_AFTER);
  }, [prefs, key, flush]);

  /* Leaving the page inside the 600ms still saves what was just changed. */
  useEffect(() => flush, [flush]);

  /** A patch, or a function of the current prefs that returns the next ones. */
  const set = useCallback(change => {
    touched.current = true;
    setPrefs(prev => ({
      ...(typeof change === 'function' ? change(prev) : { ...prev, ...change }),
      /* When it was changed, which is how a copy that never reached the
         server is told apart from an older one that did. */
      at: Date.now()
    }));
  }, []);

  /** Back to the defaults for `keys` (all of them if none are named). */
  const reset = useCallback(keys => set(prev => {
    const next = { ...prev };
    (keys || Object.keys(base.current)).forEach(k => {
      if (k in base.current) next[k] = base.current[k];
      else delete next[k];
    });
    return next;
  }), [set]);

  return { prefs, set, reset, loaded, defaults: base.current };
}
